/**
 * Customer Validation Helpers (Frontend)
 * Shared validation for SIRET, VAT number, and ZIP code formats
 */

/**
 * Validate French SIRET number format (14 digits)
 */
export function isValidSiret(siret: string): boolean {
  if (!siret) return true;
  const cleaned = siret.replace(/\s/g, "");
  return /^\d{14}$/.test(cleaned);
}

/**
 * Validate EU intracommunautaire VAT number format
 * Format: 2-letter country code + up to 12 alphanumeric characters
 */
export function isValidVatNumber(vatNumber: string): boolean {
  if (!vatNumber) return true;
  const cleaned = vatNumber.replace(/\s/g, "").toUpperCase();
  return /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(cleaned);
}

/**
 * Validate ZIP code format (5 digits for France by default)
 */
export function isValidZipCode(zipCode: string, country?: string): boolean {
  if (!zipCode) return true;
  const cleaned = zipCode.trim();
  if (country === "FR" || !country) {
    return /^\d{5}$/.test(cleaned);
  }
  return /^[A-Z0-9\s-]{3,10}$/i.test(cleaned);
}

/**
 * Get validation error message for a field
 */
export function getValidationError(
  field: string,
  value: string,
): string | null {
  switch (field) {
    case "siret":
      return !isValidSiret(value) ? "SIRET must be exactly 14 digits" : null;
    case "vat_number":
      return !isValidVatNumber(value)
        ? "VAT number must start with a 2-letter country code followed by 2-12 alphanumeric characters"
        : null;
    case "zip_code":
      return !isValidZipCode(value) ? "ZIP code must be 5 digits" : null;
    default:
      return null;
  }
}

const customerValidation = {
  isValidSiret,
  isValidVatNumber,
  isValidZipCode,
  getValidationError,
};

export default customerValidation;
